import { computed } from "vue";
import { useToastsStore } from "@/pinia/toastsStore";

// Which environments the current host may hand its Okta session off to.
// Detection is by hostname because every deploy is built with `--mode production`,
// so import.meta.env.MODE can't tell staging and prod apart.
const ENV_HOPS = {
  "pulse.redfast.com": [
    { label: "PProd", url: "https://pulsepp.redfast.com/" },
    { label: "Localhost", url: "http://localhost:8080/" },
  ],
  "pulserf1.redfastlabs.com": [{ label: "Localhost", url: "http://localhost:8080/" }],
  "pulserf2.redfastlabs.com": [{ label: "Localhost", url: "http://localhost:8080/" }],
};

const HOP_WARNING =
  "Advanced feature. This environment may run broken or unreleased features and use different data. Your current session token is carried over through the URL. Click at your own risk.";

/**
 * Cross-environment Okta session hop for internal admins.
 *
 * Exposes the environments the current host may hand its session off to and a
 * handler that carries the current Okta `id_token` to the target through
 * `?auth_token=`, which the target app reads on load and stores as its own token.
 * The handler only redirects to targets in the current host's env-hop list, so a
 * stray URL can't be used to leak the session token elsewhere.
 *
 * @returns {{ envHops: import("vue").ComputedRef<Array<{label: string, url: string}>>, openInEnv: (url: string) => void, hopWarning: string }} Targets for the current host, the redirect handler, and the advisory shown on each button.
 * @example
 * const { envHops, openInEnv } = useEnvHop();
 * // envHops.value -> [{ label: "Localhost", url: "http://localhost:8080/" }]
 * openInEnv(envHops.value[0].url); // redirects with ?auth_token=<id_token>
 */
export function useEnvHop() {
  const toastsStore = useToastsStore();

  const envHops = computed(() => ENV_HOPS[window.location.hostname] ?? []);

  const openInEnv = url => {
    if (!envHops.value.some(hop => hop.url === url)) {
      toastsStore.create({ type: "error", body: "Unknown environment target." });
      return;
    }
    const token = localStorage.getItem("id_token");
    if (!token) {
      toastsStore.create({ type: "error", body: "No session token found. Log in first." });
      return;
    }
    const target = new URL(url);
    target.searchParams.set("auth_token", token);
    window.location.href = target.toString();
  };

  return { envHops, openInEnv, hopWarning: HOP_WARNING };
}
